import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, FileText, Plus } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { useFiledDocuments } from '@/hooks/use-filed-documents';
import type { FiledDocument, FiledDocumentCategory, FiledDocumentFilters } from '@/lib/types';
import { resolveMediaUrl } from '@/lib/api-client';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';

const CATEGORY_LABELS: Record<FiledDocumentCategory, string> = {
  insurance_certificate: 'Insurance Certificate',
  client_contract: 'Client Contract',
  policy: 'Policy',
  license: 'License',
  permit: 'Permit',
  certificate: 'Certificate',
  other: 'Other',
};

function ExpiryBadge({ doc }: { doc: FiledDocument }) {
  if (!doc.expiry_date) {
    return <Badge variant="outline">No expiry</Badge>;
  }

  const days = doc.days_until_expiry ?? 0;
  if (days < 0) {
    return (
      <Badge variant="destructive" className="inline-flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" />
        Expired
      </Badge>
    );
  }

  if (days <= 30) {
    return (
      <Badge variant="secondary" className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 hover:bg-amber-100">
        <Clock className="h-3 w-3" />
        Expiring soon
      </Badge>
    );
  }

  return <Badge variant="outline">Expires {formatDateOnly(doc.expiry_date)}</Badge>;
}

function FiledDocumentRow({ doc }: { doc: FiledDocument }) {
  const timeZone = useOrgTimeZone();
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 shrink-0 text-gray-400" />
            <h3 className="truncate text-sm font-semibold text-gray-900">{doc.title}</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <Badge variant="outline">{CATEGORY_LABELS[doc.category]}</Badge>
            <ExpiryBadge doc={doc} />
            {doc.attached_label ? (
              <Badge variant="secondary">Attached to {doc.attached_label}</Badge>
            ) : (
              <Badge variant="secondary">Org-level</Badge>
            )}
          </div>
          {doc.description && <p className="text-sm text-gray-600">{doc.description}</p>}
          <p className="text-xs text-gray-500">
            Updated {formatDateTimeInTimeZone(doc.updated_at, timeZone)}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {doc.file && (
            <Button variant="outline" size="sm" asChild>
              <a href={resolveMediaUrl(doc.file)} target="_blank" rel="noreferrer">
                Open file
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function DocumentsPage() {
  const [filters, setFilters] = useState<FiledDocumentFilters>({ page: 1, page_size: 25 });
  const { data, isLoading, error } = useFiledDocuments(filters);

  const documents = data?.results ?? [];
  const categoryValue = filters.category || 'all';
  const expiringValue = filters.expiring_within ? String(filters.expiring_within) : 'all';
  const orgLevel = filters.org_level === true;

  const pageCount = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil(data.count / (filters.page_size ?? 25)));
  }, [data, filters.page_size]);

  const updateFilters = (next: Partial<FiledDocumentFilters>) => {
    setFilters((prev) => ({ ...prev, ...next, page: 1 }));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          <p className="mt-1 text-sm text-gray-600">
            Filed documents for certificates, contracts, licenses, permits, and policies.
          </p>
        </div>
        <Link to="/documents/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Upload document
          </Button>
        </Link>
      </div>

      <Card>
        <CardContent className="grid gap-3 pt-6 sm:grid-cols-3 lg:grid-cols-4">
          <div className="sm:col-span-1">
            <Select value={categoryValue} onValueChange={(value) => updateFilters({ category: value === 'all' ? undefined : (value as FiledDocumentCategory) })}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-1">
            <Select
              value={expiringValue}
              onValueChange={(value) =>
                updateFilters({ expiring_within: value === 'all' ? undefined : Number(value) })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Expiring soon" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All expiries</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-1">
            <Button
              type="button"
              variant={orgLevel ? 'default' : 'outline'}
              className="w-full"
              onClick={() => updateFilters({ org_level: orgLevel ? undefined : true })}
            >
              {orgLevel ? 'Org-level only' : 'Show org-level only'}
            </Button>
          </div>

          <div className="sm:col-span-1 flex items-center justify-end text-sm text-gray-500">
            {data ? `${data.count} document${data.count === 1 ? '' : 's'}` : 'Loading…'}
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6 text-sm text-red-700">
            Failed to load filed documents.
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="grid gap-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="animate-pulse">
              <CardContent className="h-24" />
            </Card>
          ))}
        </div>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-gray-500">
            No filed documents found for the selected filters.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {documents.map((doc) => (
            <FiledDocumentRow key={doc.id} doc={doc} />
          ))}
        </div>
      )}

      {data && pageCount > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, (prev.page ?? 1) - 1) }))}
            disabled={!data.previous}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-500">
            Page {filters.page ?? 1} of {pageCount}
          </span>
          <Button
            variant="outline"
            onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
            disabled={!data.next}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
